import {
  EventData,
  Milestone,
  Reward,
  UserMilestoneProgress,
} from '@wwe-portal/entities/rbe.type';
import Image from 'next/image';
import {
  createPortableDialog,
  Dialog,
  DialogCloseButton,
  DialogContent,
  showDialog,
  usePortableDialog,
} from '@wwe-portal/ui/components/dialog';
import styles from './milestone-reward-item.module.css';
import { RewardListDescription } from '@wwe-portal/ui/features/reward/reward-list-description';
import { formatThousands } from '@wwe-portal/utils/format';
import { useIsAuthenticating } from '@wwe-portal/web/providers/stores/auth-store';
import { useClaimReward } from './use-claim-event-milestone';
import { ClaimStatus } from './type';
import { ClaimButton } from './claim-button';
import { sendRBERewardsClaimed } from '@wwe-portal/web/libs/analytics/rbe';

const getRewardImage = (imageUrl: string) => {
  const parsedImageUrl = JSON.parse(imageUrl);
  return parsedImageUrl.url;
};

const MilestoneRewardDialog = createPortableDialog(
  ({
    requirement,
    rewards,
    status,
    eventId,
    milestoneId,
    totalPoints,
  }: {
    requirement: number;
    rewards: Reward[];
    status: ClaimStatus;
    eventId: number;
    milestoneId: string;
    totalPoints?: number;
  }) => {
    const modal = usePortableDialog();
    const { claim, isPending } = useClaimReward({ eventId, milestoneId });
    return (
      <Dialog
        open={modal.visible}
        onOpenChange={(value) => {
          if (!value) {
            modal.remove();
          }
        }}
      >
        <DialogContent variant="message">
          <div className="bg-back400 rounded-xl px-4 py-8">
            <div className={styles.rewardItemBackground}>
              <div className="flex h-full items-center justify-center gap-2.5">
                {rewards.map((item) => (
                  <div key={item.itemId} className="h-[50px]">
                    <Image
                      src={getRewardImage(item.imageUrl)}
                      alt={item.displayName}
                      width={50}
                      height={50}
                      className="h-[50px] object-contain"
                    />
                    <div className="text-text300 text-2xs mt-1 text-center font-semibold">
                      x{formatThousands(item.quantity)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="text-text400 mt-2.5 text-xs">
              Earn {requirement} Points
            </div>
            <div className="text-text400 mt-2.5 text-xs">
              <RewardListDescription
                rewards={rewards.map(
                  (item) =>
                    `${formatThousands(item.quantity)} ${item.displayName}`
                )}
              />
            </div>
            <div className="mt-5">
              <ClaimButton
                status={status}
                onClick={claim}
                isLoading={isPending}
                className="w-full"
                requiredPoint={requirement}
                totalPoints={totalPoints}
              />
            </div>
          </div>
          <DialogCloseButton onClose={() => modal.remove()} />
        </DialogContent>
      </Dialog>
    );
  }
);

const useGetMilestoneStatus = ({
  userProgress,
}: {
  userProgress: UserMilestoneProgress | undefined;
}) => {
  const authenticating = useIsAuthenticating();

  if (authenticating || !userProgress) return 'unknown';
  if (userProgress.type === 'claim') return 'unclaimed';
  if (userProgress.type === 'claimed') return 'claimed';
  return 'disabled';
};

interface MilestoneRewardProps {
  event: EventData;
  milestoneId: string;
  data: Milestone;
  userProgress: UserMilestoneProgress | undefined;
  totalPoints: number | undefined;
}

export const useMilestoneRewardItem = ({
  event,
  milestoneId,
  data,
  userProgress,
  totalPoints,
}: MilestoneRewardProps) => {
  const eventId = event.portalEventId;
  const { rewards, requirement } = data;
  const status: ClaimStatus = useGetMilestoneStatus({ userProgress });

  const top3Rewards = rewards.slice(0, 3);
  const { claim: callClaim, isPending } = useClaimReward({
    eventId,
    milestoneId,
  });
  const showMoreInfo = () => {
    showDialog(MilestoneRewardDialog, {
      requirement,
      status,
      rewards,
      eventId,
      milestoneId,
      totalPoints,
    });
  };

  const claim = () => {
    callClaim();
    sendRBERewardsClaimed(event, milestoneId);
  };

  return {
    requirement,
    top3Rewards,
    status,
    showMoreInfo,
    claim,
    isPending,
  };
};
